import EventBus from 'eventing-bus';
import io from 'socket.io-client';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.workflowSocket = null; // Separate socket for workflow namespace
    this._isConnected = false;
    this._workflowConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.subscriptions = new Map();
  }

  connect(token) {
    if (this.socket && this._isConnected) {
      console.log('[WebSocket] Already connected');
      return Promise.resolve();
    }

    console.log('[WebSocket] Connecting with token:', token ? 'present' : 'missing');

    return new Promise((resolve, reject) => {
      try {
        // Get base URL from current environment
        const baseURL = process.env.REACT_APP_API_URL || window.location.origin;
        console.log('[WebSocket] Connecting to:', baseURL);
        
        this.socket = io(baseURL, {
          auth: { token },
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: this.maxReconnectAttempts,
          reconnectionDelay: this.reconnectDelay
        });

        this.socket.on('connect', () => {
          console.log('WebSocket connected');
          this._isConnected = true;
          this.reconnectAttempts = 0;
          EventBus.publish('websocket:connected');
          
          // Now connect to workflow namespace
          this.connectWorkflowNamespace(token, baseURL);
          resolve();
        });

        this.socket.on('disconnect', (reason) => {
          console.log('WebSocket disconnected:', reason);
          this._isConnected = false;
          EventBus.publish('websocket:disconnected', reason);
        });

        this.socket.on('connect_error', (error) => {
          console.error('WebSocket connection error:', error);
          this.reconnectAttempts++;
          
          if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            EventBus.publish('websocket:error', 'Failed to connect to server');
            reject(error);
          }
        });

        // Workflow-specific events will be handled in the workflow namespace

        // Workspace events
        this.socket.on('workspace:created', (data) => {
          EventBus.publish('workspace:created', data);
          this.notifySubscribers('workspace:created', data);
        });

        this.socket.on('workspace:updated', (data) => {
          EventBus.publish('workspace:updated', data);
          this.notifySubscribers('workspace:updated', data);
        });

        this.socket.on('workspace:deleted', (data) => {
          EventBus.publish('workspace:deleted', data);
          this.notifySubscribers('workspace:deleted', data);
        });

        // General events
        this.socket.on('notification', (data) => {
          EventBus.publish('notification', data);
        });

        this.socket.on('error', (error) => {
          console.error('WebSocket error:', error);
          EventBus.publish('websocket:error', error);
        });

      } catch (error) {
        console.error('Failed to initialize WebSocket:', error);
        reject(error);
      }
    });
  }

  connectWorkflowNamespace(token, baseURL) {
    if (this.workflowSocket && this._workflowConnected) {
      console.log('[WorkflowSocket] Already connected');
      return;
    }

    console.log('[WorkflowSocket] Connecting to workflow namespace');
    
    this.workflowSocket = io(`${baseURL}/workflow-execution`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay
    });

    this.workflowSocket.on('connect', () => {
      console.log('[WorkflowSocket] Connected to workflow namespace');
      this._workflowConnected = true;
      EventBus.publish('workflow:connected');
    });

    this.workflowSocket.on('disconnect', (reason) => {
      console.log('[WorkflowSocket] Disconnected from workflow namespace:', reason);
      this._workflowConnected = false;
      EventBus.publish('workflow:disconnected', reason);
    });

    // Workflow execution events
    this.workflowSocket.on('workflow:start', (data) => {
      console.log('[WorkflowSocket] Received workflow:start event:', data);
      EventBus.publish('workflow:start', data);
      this.notifySubscribers('workflow:start', data);
    });

    this.workflowSocket.on('workflow:complete', (data) => {
      console.log('[WorkflowSocket] Received workflow:complete event:', data);
      EventBus.publish('workflow:execution:completed', data);
      this.notifySubscribers('workflow:complete', data);
    });

    this.workflowSocket.on('workflow:error', (data) => {
      console.log('[WorkflowSocket] Received workflow:error event:', data);
      EventBus.publish('workflow:execution:failed', data);
      this.notifySubscribers('workflow:error', data);
    });

    this.workflowSocket.on('workflow:node:start', (data) => {
      console.log('[WorkflowSocket] Received workflow:node:start event:', data);
      EventBus.publish('workflow:node:started', data);
      this.notifySubscribers('workflow:node:start', data);
    });

    this.workflowSocket.on('workflow:node:complete', (data) => {
      console.log('[WorkflowSocket] Received workflow:node:complete event:', data);
      EventBus.publish('workflow:node:completed', data);
      // Make sure to include timestamp if not present
      const eventData = {
        ...data,
        timestamp: data.timestamp || new Date().toISOString()
      };
      this.notifySubscribers('workflow:node:complete', eventData);
    });

    this.workflowSocket.on('workflow:node:error', (data) => {
      console.log('[WorkflowSocket] Received workflow:node:error event:', data);
      EventBus.publish('workflow:node:failed', data);
      this.notifySubscribers('workflow:node:error', data);
    });

    this.workflowSocket.on('workflow:node:skip', (data) => {
      console.log('[WorkflowSocket] Received workflow:node:skip event:', data);
      EventBus.publish('workflow:node:skipped', data);
      this.notifySubscribers('workflow:node:skip', data);
    });

    this.workflowSocket.on('workflow:node:progress', (data) => {
      console.log('[WorkflowSocket] Received workflow:node:progress event:', data);
      EventBus.publish('workflow:node:progress', data);
      this.notifySubscribers('workflow:node:progress', data);
    });

    this.workflowSocket.on('error', (error) => {
      console.error('[WorkflowSocket] Error:', error);
      EventBus.publish('workflow:error', error);
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this._isConnected = false;
    }
    
    if (this.workflowSocket) {
      this.workflowSocket.disconnect();
      this.workflowSocket = null;
      this._workflowConnected = false;
    }
    
    this.subscriptions.clear();
  }

  // Subscribe to execution updates for a specific execution
  subscribeToExecution(executionId, callback) {
    if (!this.socket || !this._isConnected) {
      console.error('WebSocket not connected', { 
        hasSocket: !!this.socket, 
        isConnected: this._isConnected 
      });
      return;
    }

    const subscriptionKey = `execution:${executionId}`;
    
    // Store the callback
    if (!this.subscriptions.has(subscriptionKey)) {
      this.subscriptions.set(subscriptionKey, new Set());
    }
    this.subscriptions.get(subscriptionKey).add(callback);

    // Join the execution room
    console.log('[WebSocket] Subscribing to execution:', executionId);
    this.socket.emit('workflow:execution:subscribe', { executionId });

    // Return unsubscribe function
    return () => {
      this.unsubscribeFromExecution(executionId, callback);
    };
  }

  unsubscribeFromExecution(executionId, callback) {
    const subscriptionKey = `execution:${executionId}`;
    const callbacks = this.subscriptions.get(subscriptionKey);
    
    if (callbacks) {
      callbacks.delete(callback);
      
      if (callbacks.size === 0) {
        this.subscriptions.delete(subscriptionKey);
        
        if (this.socket && this._isConnected) {
          this.socket.emit('workflow:execution:unsubscribe', { executionId });
        }
      }
    }
  }

  // Subscribe to workflow updates (for all executions of a workflow)
  subscribeToWorkflow(workflowId, callback) {
    if (!this.socket || !this._isConnected) {
      console.error('WebSocket not connected');
      return;
    }

    const subscriptionKey = `workflow:${workflowId}`;
    
    if (!this.subscriptions.has(subscriptionKey)) {
      this.subscriptions.set(subscriptionKey, new Set());
    }
    this.subscriptions.get(subscriptionKey).add(callback);

    this.socket.emit('workflow:subscribe', { workflowId });

    return () => {
      this.unsubscribeFromWorkflow(workflowId, callback);
    };
  }

  unsubscribeFromWorkflow(workflowId, callback) {
    const subscriptionKey = `workflow:${workflowId}`;
    const callbacks = this.subscriptions.get(subscriptionKey);
    
    if (callbacks) {
      callbacks.delete(callback);
      
      if (callbacks.size === 0) {
        this.subscriptions.delete(subscriptionKey);
        
        if (this.socket && this._isConnected) {
          this.socket.emit('workflow:unsubscribe', { workflowId });
        }
      }
    }
  }

  // Notify all subscribers for a specific event
  notifySubscribers(eventType, data) {
    // Notify execution-specific subscribers
    if (data.executionId) {
      const executionCallbacks = this.subscriptions.get(`execution:${data.executionId}`);
      if (executionCallbacks) {
        executionCallbacks.forEach(callback => {
          try {
            callback(eventType, data);
          } catch (error) {
            console.error('Error in execution subscriber callback:', error);
          }
        });
      }
    }

    // Notify workflow-specific subscribers
    if (data.workflowId) {
      const workflowCallbacks = this.subscriptions.get(`workflow:${data.workflowId}`);
      if (workflowCallbacks) {
        workflowCallbacks.forEach(callback => {
          try {
            callback(eventType, data);
          } catch (error) {
            console.error('Error in workflow subscriber callback:', error);
          }
        });
      }
    }
  }

  // Emit custom events
  emit(event, data, namespace = 'main') {
    if (namespace === 'workflow') {
      if (!this.workflowSocket || !this._workflowConnected) {
        console.error('Workflow WebSocket not connected');
        return;
      }
      this.workflowSocket.emit(event, data);
    } else {
      if (!this.socket || !this._isConnected) {
        console.error('WebSocket not connected');
        return;
      }
      this.socket.emit(event, data);
    }
  }

  // Check connection status
  isConnected(namespace = 'main') {
    if (namespace === 'workflow') {
      return this._workflowConnected && this.workflowSocket?.connected;
    }
    return this._isConnected && this.socket?.connected;
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;